import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { PaperPlaneRight, Robot, X } from "@phosphor-icons/react";

interface Message {
  id: string;
  sender: 'user' | 'bot';
  text: string;
  timestamp: Date;
}

export function AIAssistant() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { id: '1', sender: 'bot', text: 'Hi! I\'m FlowBot. How can I help you today?', timestamp: new Date() }
  ]);
  const [inputValue, setInputValue] = useState("");

  const handleSend = () => {
    if (!inputValue.trim()) return;

    const userMsg: Message = { id: Date.now().toString(), sender: 'user', text: inputValue, timestamp: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setInputValue("");

    // Simulate AI response
    setTimeout(() => {
      let botResponse = "I'm not sure about that.";
      const lowerInput = userMsg.text.toLowerCase();

      if (lowerInput.includes('leave')) botResponse = "You have 12 days of Annual Leave remaining.";
      else if (lowerInput.includes('policy')) botResponse = "You can find company policies in the Core HR section under Documents.";
      else if (lowerInput.includes('salary') || lowerInput.includes('payslip')) botResponse = "Your latest payslip for January 2026 is available in the Payroll section.";
      else if (lowerInput.includes('manager')) botResponse = "Your reporting manager is Sarah Jenkins.";

      const botMsg: Message = { id: (Date.now() + 1).toString(), sender: 'bot', text: botResponse, timestamp: new Date() };
      setMessages(prev => [...prev, botMsg]);
    }, 1000);
  };

  if (!isOpen) {
    return (
      <Button 
        className="fixed bottom-6 right-6 rounded-full w-14 h-14 shadow-lg z-50 p-0 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
        onClick={() => setIsOpen(true)}
      >
        <Robot className="w-8 h-8 text-white" />
      </Button>
    );
  }

  return (
    <Card className="fixed bottom-6 right-6 w-80 md:w-96 h-[500px] shadow-2xl z-50 flex flex-col animate-in slide-in-from-bottom-10 fade-in border-blue-100 dark:border-slate-700">
      <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-t-lg p-4 flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="bg-white/20 p-1.5 rounded-full">
            <Robot className="w-5 h-5" />
          </div>
          <div>
            <CardTitle className="text-sm font-medium">FlowBot AI</CardTitle>
            <p className="text-xs text-blue-100">Always here to help</p>
          </div>
        </div>
        <Button variant="ghost" size="icon" className="text-white hover:bg-white/20 h-8 w-8" onClick={() => setIsOpen(false)}>
          <X className="w-4 h-4" />
        </Button>
      </CardHeader>
      
      <CardContent className="flex-1 p-0 overflow-hidden bg-slate-50 dark:bg-slate-900">
        <ScrollArea className="h-full p-4">
          <div className="space-y-4">
            {messages.map((msg) => (
              <div key={msg.id} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${
                  msg.sender === 'user' 
                    ? 'bg-blue-600 text-white rounded-br-none' 
                    : 'bg-white dark:bg-slate-800 border shadow-sm rounded-bl-none'
                }`}>
                  {msg.text}
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </CardContent>

      <CardFooter className="p-3 border-t bg-white dark:bg-slate-800">
        <form 
          className="flex w-full gap-2"
          onSubmit={(e) => { e.preventDefault(); handleSend(); }}
        >
          <Input 
            placeholder="Ask me anything..." 
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            className="flex-1"
          />
          <Button type="submit" size="icon" disabled={!inputValue.trim()}>
            <PaperPlaneRight className="w-4 h-4" />
          </Button>
        </form>
      </CardFooter>
    </Card>
  );
}
